import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { useLocation } from 'react-router-dom';
import { matchesSiteHeaderDesktopScrollDirection } from './siteHeaderAppearance';
const MOBILE_BREAKPOINT_PX = 768;
const HIDE_DOWN_THRESHOLD_PX = 64;
const SHOW_UP_THRESHOLD_PX = 24;
const JITTER_IGNORE_PX = 2;
const DEFAULT_CONTEXT = {
    isMobile: false,
    isHeaderHidden: false,
    headerHiddenSource: 'none',
    isMobileHeaderHidden: false,
    mobileHeaderHiddenSource: 'none',
    registerZone: () => () => undefined,
    updateZoneActive: () => undefined,
};
const MobileHeaderPushContext = React.createContext(DEFAULT_CONTEXT);
function getCurrentScrollY() {
    if (typeof window === 'undefined') {
        return 0;
    }
    return Math.max(0, window.scrollY);
}
function createScrollDirectionState(initialY) {
    const safeY = Math.max(0, initialY);
    return {
        lastY: safeY,
        downAccum: 0,
        upAccum: 0,
        hidden: false,
    };
}
function hasPushZonePriority(zones) {
    return Object.values(zones).some((zone) => {
        if (zone.mode === 'while-active') {
            return zone.active;
        }
        return zone.latched;
    });
}
function resolveIsScrollDirectionEnabled(isMobile, pathname) {
    if (isMobile) {
        return true;
    }
    return matchesSiteHeaderDesktopScrollDirection(pathname);
}
function advanceScrollDirectionState(scrollState, scrollY, isEnabled) {
    const nextY = Math.max(0, scrollY);
    if (!isEnabled) {
        if (scrollState.lastY === nextY
            && scrollState.downAccum === 0
            && scrollState.upAccum === 0
            && !scrollState.hidden) {
            return scrollState;
        }
        return createScrollDirectionState(nextY);
    }
    if (nextY <= 0) {
        if (scrollState.lastY === 0
            && scrollState.downAccum === 0
            && scrollState.upAccum === 0
            && !scrollState.hidden) {
            return scrollState;
        }
        return createScrollDirectionState(0);
    }
    const delta = nextY - scrollState.lastY;
    if (Math.abs(delta) <= JITTER_IGNORE_PX) {
        if (scrollState.lastY === nextY) {
            return scrollState;
        }
        return {
            ...scrollState,
            lastY: nextY,
        };
    }
    if (delta > 0) {
        const nextDownAccum = scrollState.downAccum + delta;
        const nextHidden = scrollState.hidden || nextDownAccum >= HIDE_DOWN_THRESHOLD_PX;
        return {
            lastY: nextY,
            downAccum: nextDownAccum,
            upAccum: 0,
            hidden: nextHidden,
        };
    }
    const nextUpAccum = scrollState.upAccum + Math.abs(delta);
    const nextHidden = nextUpAccum >= SHOW_UP_THRESHOLD_PX ? false : scrollState.hidden;
    return {
        lastY: nextY,
        downAccum: 0,
        upAccum: nextUpAccum,
        hidden: nextHidden,
    };
}
function createInitialState(pathname) {
    if (typeof window === 'undefined') {
        return {
            isMobile: false,
            isScrollDirectionEnabled: false,
            zones: {},
            scroll: createScrollDirectionState(0),
        };
    }
    const initialScrollY = getCurrentScrollY();
    return {
        isMobile: window.innerWidth < MOBILE_BREAKPOINT_PX,
        isScrollDirectionEnabled: resolveIsScrollDirectionEnabled(window.innerWidth < MOBILE_BREAKPOINT_PX, pathname),
        zones: {},
        scroll: createScrollDirectionState(initialScrollY),
    };
}
function reducer(state, action) {
    switch (action.type) {
        case 'viewport-tick': {
            const nextIsMobile = action.isMobile;
            const nextIsScrollDirectionEnabled = action.isScrollDirectionEnabled;
            const nextScrollState = advanceScrollDirectionState(state.scroll, action.scrollY, nextIsScrollDirectionEnabled);
            let zonesChanged = false;
            let nextZones = state.zones;
            if (action.scrollY <= 0) {
                nextZones = {};
                Object.entries(state.zones).forEach(([id, zone]) => {
                    if (zone.mode === 'until-top' && zone.latched) {
                        zonesChanged = true;
                        nextZones[id] = { ...zone, latched: false };
                    }
                    else {
                        nextZones[id] = zone;
                    }
                });
            }
            if (state.isMobile === nextIsMobile
                && state.isScrollDirectionEnabled === nextIsScrollDirectionEnabled
                && state.scroll.lastY === nextScrollState.lastY
                && state.scroll.downAccum === nextScrollState.downAccum
                && state.scroll.upAccum === nextScrollState.upAccum
                && state.scroll.hidden === nextScrollState.hidden
                && !zonesChanged) {
                return state;
            }
            return {
                ...state,
                isMobile: nextIsMobile,
                isScrollDirectionEnabled: nextIsScrollDirectionEnabled,
                zones: nextZones,
                scroll: nextScrollState,
            };
        }
        case 'register-zone': {
            const prevZone = state.zones[action.id];
            const nextZone = prevZone
                ? { ...prevZone, mode: action.mode }
                : { mode: action.mode, active: false, latched: false };
            if (prevZone != null && prevZone.mode === nextZone.mode) {
                return state;
            }
            return {
                ...state,
                zones: {
                    ...state.zones,
                    [action.id]: nextZone,
                },
            };
        }
        case 'unregister-zone': {
            if (!(action.id in state.zones)) {
                return state;
            }
            const nextZones = { ...state.zones };
            delete nextZones[action.id];
            return {
                ...state,
                zones: nextZones,
            };
        }
        case 'set-zone-active': {
            const zone = state.zones[action.id];
            if (!zone) {
                return state;
            }
            const nextLatched = zone.mode === 'until-top' ? zone.latched || action.active : false;
            if (zone.active === action.active && zone.latched === nextLatched) {
                return state;
            }
            return {
                ...state,
                zones: {
                    ...state.zones,
                    [action.id]: {
                        ...zone,
                        active: action.active,
                        latched: nextLatched,
                    },
                },
            };
        }
        case 'rebase-scroll-base': {
            const nextScrollState = createScrollDirectionState(action.scrollY);
            if (state.scroll.lastY === nextScrollState.lastY
                && state.scroll.downAccum === nextScrollState.downAccum
                && state.scroll.upAccum === nextScrollState.upAccum
                && state.scroll.hidden === nextScrollState.hidden) {
                return state;
            }
            return {
                ...state,
                scroll: nextScrollState,
            };
        }
        case 'reset-route-state': {
            const nextIsScrollDirectionEnabled = action.isScrollDirectionEnabled;
            let changed = false;
            const nextZones = {};
            Object.entries(state.zones).forEach(([id, zone]) => {
                if (zone.active || zone.latched) {
                    changed = true;
                    nextZones[id] = { ...zone, active: false, latched: false };
                }
                else {
                    nextZones[id] = zone;
                }
            });
            const nextScrollState = createScrollDirectionState(action.scrollY);
            const scrollChanged = (state.scroll.lastY !== nextScrollState.lastY
                || state.scroll.downAccum !== nextScrollState.downAccum
                || state.scroll.upAccum !== nextScrollState.upAccum
                || state.scroll.hidden !== nextScrollState.hidden);
            const scrollDirectionChanged = state.isScrollDirectionEnabled !== nextIsScrollDirectionEnabled;
            if (!changed && !scrollChanged && !scrollDirectionChanged) {
                return state;
            }
            return {
                ...state,
                isScrollDirectionEnabled: nextIsScrollDirectionEnabled,
                zones: changed ? nextZones : state.zones,
                scroll: nextScrollState,
            };
        }
        default:
            return state;
    }
}
export const MobileHeaderPushProvider = ({ children }) => {
    const { pathname } = useLocation();
    const [state, dispatch] = React.useReducer(reducer, pathname, createInitialState);
    React.useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }
        let frame = 0;
        const scheduleViewportUpdate = () => {
            if (frame !== 0) {
                return;
            }
            frame = window.requestAnimationFrame(() => {
                frame = 0;
                dispatch({
                    type: 'viewport-tick',
                    isMobile: window.innerWidth < MOBILE_BREAKPOINT_PX,
                    scrollY: getCurrentScrollY(),
                    isScrollDirectionEnabled: resolveIsScrollDirectionEnabled(window.innerWidth < MOBILE_BREAKPOINT_PX, pathname),
                });
            });
        };
        scheduleViewportUpdate();
        window.addEventListener('resize', scheduleViewportUpdate);
        window.addEventListener('scroll', scheduleViewportUpdate, { passive: true });
        return () => {
            window.removeEventListener('resize', scheduleViewportUpdate);
            window.removeEventListener('scroll', scheduleViewportUpdate);
            if (frame !== 0) {
                window.cancelAnimationFrame(frame);
            }
        };
    }, [pathname]);
    React.useEffect(() => {
        const isMobile = typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT_PX;
        dispatch({
            type: 'reset-route-state',
            scrollY: getCurrentScrollY(),
            isScrollDirectionEnabled: resolveIsScrollDirectionEnabled(isMobile, pathname),
        });
    }, [pathname]);
    const registerZone = React.useCallback((id, mode) => {
        dispatch({ type: 'register-zone', id, mode });
        return () => {
            dispatch({ type: 'unregister-zone', id });
        };
    }, []);
    const updateZoneActive = React.useCallback((id, active) => {
        dispatch({ type: 'set-zone-active', id, active });
    }, []);
    const pushZoneHidden = React.useMemo(() => hasPushZonePriority(state.zones), [state.zones]);
    const previousPushZoneHiddenRef = React.useRef(pushZoneHidden);
    React.useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const previousPushZoneHidden = previousPushZoneHiddenRef.current;
        if (previousPushZoneHidden && !pushZoneHidden) {
            dispatch({ type: 'rebase-scroll-base', scrollY: getCurrentScrollY() });
        }
        previousPushZoneHiddenRef.current = pushZoneHidden;
    }, [pushZoneHidden]);
    const { isHeaderHidden, headerHiddenSource } = React.useMemo(() => {
        if (pushZoneHidden) {
            return {
                isHeaderHidden: true,
                headerHiddenSource: 'push-zone',
            };
        }
        if (state.scroll.hidden) {
            return {
                isHeaderHidden: true,
                headerHiddenSource: 'scroll-direction',
            };
        }
        return {
            isHeaderHidden: false,
            headerHiddenSource: 'none',
        };
    }, [pushZoneHidden, state.scroll.hidden]);
    const value = React.useMemo(() => ({
        isMobile: state.isMobile,
        isHeaderHidden,
        headerHiddenSource,
        isMobileHeaderHidden: isHeaderHidden,
        mobileHeaderHiddenSource: headerHiddenSource,
        registerZone,
        updateZoneActive,
    }), [state.isMobile, isHeaderHidden, headerHiddenSource, registerZone, updateZoneActive]);
    return (_jsx(MobileHeaderPushContext.Provider, { value: value, children: children }));
};
export function useMobileHeaderPush() {
    return React.useContext(MobileHeaderPushContext);
}
